import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch
} from '@mui/material';
import { Delete, Edit, Add, Place, DirectionsBike, Close, Visibility, VisibilityOff, FileUpload, Business, ExpandMore, ExpandLess, CheckBox, CheckBoxOutlineBlank, Description, BarChart as BarChartIcon } from '@mui/icons-material';
import ParkingsStatsSection from './components/ParkingsStatsSection';
import TextField from '@mui/material/TextField';
import { GoogleMap, Polygon, Marker } from '@react-google-maps/api';
import { getParkingsAsync, deleteParkingAsync, createParkingAsync, updateParkingAsync, setParkingsHiddenInAppAsync, selectParkings } from '../../redux/slices/parkingsSlice';
import { getManagementCompaniesAsync, deleteManagementCompanyAsync, selectManagementCompanies } from '../../redux/slices/managementCompaniesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectCities } from '../../redux/slices/citiesSlice';
import Title from '../../components/Title';
import TableCell from '../../components/TableCell';
import ParkingModal from '../../modals/ParkingModal';
import ManagementCompanyModal from '../../modals/ManagementCompanyModal';
import { formatDate } from '../../resources/functions';
import usePermissions from '../../hooks/usePermissions';
import { darkMapStyles } from '../../resources/constants';
import { Parking } from '../../resources/types/parkingsTypes';
import { ManagementCompany } from '../../resources/types/managementCompaniesTypes';

const defaultCenter = {
  lat: 48.9225224,
  lng: 24.7103188
};

const PREFIX = 'ParkingsPage';

const classes = {
  title: `${PREFIX}-title`
};

const Root = styled('div')({
  [`& .${classes.title}`]: {
    marginBottom: 20
  }
});

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  color: theme.palette.text.primary
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(78, 205, 196, 0.08)' 
      : 'rgba(78, 205, 196, 0.12)'
  }
}));

const PARKING_COLOR = '#4ECDC4';
const HIDDEN_PARKING_COLOR = '#9E9E9E';

const activeSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ECDC4' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4ECDC4' }
};

const getPolygonCenter = (coordinates: [number, number][]): { lat: number; lng: number } => {
  if (coordinates.length === 0) return { lat: 0, lng: 0 };
  const sum = coordinates.reduce(
    (acc, coord) => ({ lat: acc.lat + coord[0], lng: acc.lng + coord[1] }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / coordinates.length, lng: sum.lng / coordinates.length };
};

const ParkingsPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const parkingsState = useSelector(selectParkings);
  const admins = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);
  const cities = useSelector(selectCities);
  
  const checkPermission = usePermissions();
  const [parkingModalOpen, setParkingModalOpen] = useState(false);
  const [selectedParking, setSelectedParking] = useState<Parking | null>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [showParkings, setShowParkings] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [parkingToDelete, setParkingToDelete] = useState<Parking | null>(null);
  const [deleteCountdown, setDeleteCountdown] = useState(3);
  const [importing, setImporting] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<ManagementCompany | null>(null);
  const [companiesExpanded, setCompaniesExpanded] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<ManagementCompany | null>(null);
  const [companyDeleteCountdown, setCompanyDeleteCountdown] = useState(3);
  const managementCompaniesState = useSelector(selectManagementCompanies);
  
  const [kmlImportDialogOpen, setKmlImportDialogOpen] = useState(false);
  const [kmlImportCompany, setKmlImportCompany] = useState('');
  const [pendingKmlFile, setPendingKmlFile] = useState<File | null>(null);
  
  const [selectedParkings, setSelectedParkings] = useState<string[]>([]);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignCompany, setBulkAssignCompany] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteCountdown, setBulkDeleteCountdown] = useState(3);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDescDialogOpen, setBulkDescDialogOpen] = useState(false);
  const [bulkDescEn, setBulkDescEn] = useState('');
  const [bulkDescUa, setBulkDescUa] = useState('');
  const [bulkDescDe, setBulkDescDe] = useState('');
  const [bulkDescEs, setBulkDescEs] = useState('');
  const [bulkDescUpdating, setBulkDescUpdating] = useState(false);
  
  const mapRef = useRef<google.maps.Map | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const createPolygonFromPoint = (lat: number, lng: number, radiusMeters: number = 15): [number, number][] => {
    const earthRadius = 6371000;
    const latOffset = (radiusMeters / earthRadius) * (180 / Math.PI);
    const lngOffset = (radiusMeters / earthRadius) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180);
    
    return [
      [lat + latOffset, lng],
      [lat + latOffset * 0.7, lng + lngOffset * 0.7],
      [lat, lng + lngOffset],
      [lat - latOffset * 0.7, lng + lngOffset * 0.7],
      [lat - latOffset, lng],
      [lat - latOffset * 0.7, lng - lngOffset * 0.7],
      [lat, lng - lngOffset],
      [lat + latOffset * 0.7, lng - lngOffset * 0.7],
      [lat + latOffset, lng]
    ];
  };

  const parseKMLFile = useCallback((file: File, managementCompanyId: string) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setImporting(true);
        const text = e.target?.result as string;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, 'text/xml');
        
        const placemarks = xmlDoc.getElementsByTagName('Placemark');
        let imported = 0;
        
        for (let i = 0; i < placemarks.length; i++) {
          const placemark = placemarks[i];
          const nameEl = placemark.getElementsByTagName('name')[0];
          const coordsEl = placemark.getElementsByTagName('coordinates')[0];
          
          if (nameEl && coordsEl) {
            const name = nameEl.textContent?.trim() || `Парковка ${i + 1}`;
            const coordsText = coordsEl.textContent?.trim() || '';
            const parts = coordsText.split(',').map(p => parseFloat(p.trim()));
            
            if (parts.length >= 2) {
              const lng = parts[0];
              const lat = parts[1];
              
              if (!isNaN(lat) && !isNaN(lng)) {
                const polygonCoords = createPolygonFromPoint(lat, lng);
                
                await new Promise<void>((resolve) => {
                  dispatch(createParkingAsync({ 
                    name, 
                    coordinates: polygonCoords,
                    managementCompany: managementCompanyId
                  }, () => resolve()));
                });
                imported++;
              }
            }
          }
        }
        
        setImporting(false);
        setKmlImportDialogOpen(false);
        setPendingKmlFile(null);
        setKmlImportCompany('');
        alert(`Імпортовано ${imported} парковок з KML файлу`);
      } catch (error) {
        setImporting(false);
        alert('Помилка при обробці KML файлу');
        console.error(error);
      }
    };
    reader.readAsText(file);
  }, [dispatch]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith('.kml')) {
      setPendingKmlFile(file);
      setKmlImportDialogOpen(true);
    } else if (file) {
      alert('Будь ласка, виберіть файл у форматі KML');
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleConfirmKmlImport = useCallback(() => {
    if (pendingKmlFile && kmlImportCompany) {
      parseKMLFile(pendingKmlFile, kmlImportCompany);
    }
  }, [pendingKmlFile, kmlImportCompany, parseKMLFile]);

  const handleCancelKmlImport = useCallback(() => {
    setKmlImportDialogOpen(false);
    setPendingKmlFile(null);
    setKmlImportCompany('');
  }, []);

  const handleToggleParkingSelection = useCallback((parkingId: string) => {
    setSelectedParkings(prev => 
      prev.includes(parkingId) 
        ? prev.filter(id => id !== parkingId)
        : [...prev, parkingId]
    );
  }, []);

  // hiddenInApp fully disables the parking on the backend: hidden in the app and ignored for trips
  const handleToggleParkingActive = useCallback((parking: Parking) => {
    dispatch(setParkingsHiddenInAppAsync([parking._id], !parking.hiddenInApp));
  }, [dispatch]);

  const handleSelectAllParkings = useCallback(() => {
    if (selectedParkings.length === parkingsState.parkings.length) {
      setSelectedParkings([]);
    } else {
      setSelectedParkings(parkingsState.parkings.map(p => p._id));
    }
  }, [selectedParkings.length, parkingsState.parkings]);

  const handleBulkAssign = useCallback(async () => {
    if (!bulkAssignCompany || selectedParkings.length === 0) return;
    
    setBulkAssigning(true);
    try {
      for (const parkingId of selectedParkings) {
        await new Promise<void>((resolve) => {
          dispatch(updateParkingAsync(parkingId, { managementCompany: bulkAssignCompany }, () => resolve()));
        });
      }
      setBulkAssignDialogOpen(false);
      setBulkAssignCompany('');
      setSelectedParkings([]);
    } catch (error) {
      console.error('Error bulk assigning:', error);
    } finally {
      setBulkAssigning(false);
    }
  }, [bulkAssignCompany, selectedParkings, dispatch]);

  const handleBulkDescUpdate = useCallback(async () => {
    if (selectedParkings.length === 0) return;
    if (!bulkDescEn.trim() && !bulkDescUa.trim() && !bulkDescDe.trim() && !bulkDescEs.trim()) return;

    setBulkDescUpdating(true);
    try {
      const instructions: { en?: string; ua?: string; de?: string; es?: string } = {};
      if (bulkDescEn.trim()) instructions.en = bulkDescEn.trim();
      if (bulkDescUa.trim()) instructions.ua = bulkDescUa.trim();
      if (bulkDescDe.trim()) instructions.de = bulkDescDe.trim();
      if (bulkDescEs.trim()) instructions.es = bulkDescEs.trim();

      for (const parkingId of selectedParkings) {
        await new Promise<void>((resolve) => {
          dispatch(updateParkingAsync(parkingId, { instructions }, () => resolve()));
        });
      }
      setBulkDescDialogOpen(false);
      setBulkDescEn('');
      setBulkDescUa('');
      setBulkDescDe('');
      setBulkDescEs('');
      setSelectedParkings([]);
    } catch (error) {
      console.error('Error bulk updating description:', error);
    } finally {
      setBulkDescUpdating(false);
    }
  }, [selectedParkings, bulkDescEn, bulkDescUa, bulkDescDe, bulkDescEs, dispatch]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedParkings.length === 0) return;
    
    setBulkDeleting(true);
    try {
      for (const parkingId of selectedParkings) {
        await new Promise<void>((resolve) => {
          dispatch(deleteParkingAsync(parkingId, () => resolve()));
        });
      }
      setBulkDeleteDialogOpen(false);
      setSelectedParkings([]);
    } catch (error) {
      console.error('Error bulk deleting:', error);
    } finally {
      setBulkDeleting(false);
      setBulkDeleteCountdown(3);
    }
  }, [selectedParkings, dispatch]);

  useEffect(() => {
    if (bulkDeleteDialogOpen && bulkDeleteCountdown > 0) {
      const timer = setTimeout(() => {
        setBulkDeleteCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [bulkDeleteDialogOpen, bulkDeleteCountdown]);

  useEffect(() => {
    dispatch(getParkingsAsync(true));
    dispatch(getManagementCompaniesAsync(true));
  }, [dispatch]);

  const getCompanyName = useCallback((companyId?: string) => {
    if (!companyId) return '-';
    const company = managementCompaniesState.companies.find(c => c._id === companyId);
    return company ? company.name : '-';
  }, [managementCompaniesState.companies]);

  useEffect(() => {
    if (deleteConfirmOpen && deleteCountdown > 0) {
      const timer = setTimeout(() => {
        setDeleteCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [deleteConfirmOpen, deleteCountdown]);

  useEffect(() => {
    if (companyToDelete && companyDeleteCountdown > 0) {
      const timer = setTimeout(() => {
        setCompanyDeleteCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [companyToDelete, companyDeleteCountdown]);

  useEffect(() => {
    let nextMapCenter;

    if (auth.info?.type === 'superAdmin') {
      const city = cities.items.find(item => item._id === admins.currentCity);
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    } else if (admins.info && admins.currentCity) {
      const city = admins.info.cities.find(
        item => item._id === admins.currentCity
      );
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    }

    if (!nextMapCenter) {
      setMapCenter(defaultCenter);
    } else {
      setMapCenter(nextMapCenter);
    }
  }, [admins.currentCity, admins.info, auth.info, cities.items]);

  const handleOpenCreate = useCallback(() => {
    setSelectedParking(null);
    setParkingModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((parking: Parking) => {
    setSelectedParking(parking);
    setParkingModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setParkingModalOpen(false);
    setSelectedParking(null);
  }, []);

  const handleDeleteClick = useCallback((parking: Parking) => {
    setParkingToDelete(parking);
    setDeleteCountdown(3);
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (parkingToDelete) {
      dispatch(deleteParkingAsync(parkingToDelete._id, () => {
        setDeleteConfirmOpen(false);
        setParkingToDelete(null);
      }));
    }
  }, [dispatch, parkingToDelete]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setParkingToDelete(null);
  }, []);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    if (parkingsState.parkings.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      parkingsState.parkings.forEach(parking => {
        parking.coordinates.forEach(coord => {
          bounds.extend(new google.maps.LatLng(coord[0], coord[1]));
        });
      });
      map.fitBounds(bounds, 50);
    }
  }, [parkingsState.parkings]);

  useEffect(() => {
    if (mapRef.current && parkingsState.parkings.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      parkingsState.parkings.forEach(parking => {
        parking.coordinates.forEach(coord => {
          bounds.extend(new google.maps.LatLng(coord[0], coord[1]));
        });
      });
      mapRef.current.fitBounds(bounds, 50);
    }
  }, [parkingsState.parkings]);

  const focusOnParking = useCallback((parking: Parking) => {
    setShowParkings(true);
    if (mapRef.current && parking.coordinates.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      parking.coordinates.forEach(coord => {
        bounds.extend(new google.maps.LatLng(coord[0], coord[1]));
      });
      mapRef.current.fitBounds(bounds);
    }
  }, []);

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    return `https://whee.pro/public/${photoPath}`;
  };

  const scooterCount = (parking: Parking) => parking.scooters?.length || 0;

  return (
    <Root>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Title className={classes.title}>Парковки біля готелів</Title>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <input
            type="file"
            accept=".kml"
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            startIcon={importing ? <CircularProgress size={20} sx={{ color: '#4ECDC4' }} /> : <FileUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            sx={{
              borderColor: '#4ECDC4',
              color: '#4ECDC4',
              '&:hover': { borderColor: '#3BA99F', backgroundColor: 'rgba(78, 205, 196, 0.08)' }
            }}
          >
            {importing ? 'Імпорт...' : 'Імпорт KML'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              backgroundColor: '#4ECDC4',
              '&:hover': { backgroundColor: '#3BA99F' }
            }}
          >
            Додати парковку
          </Button>
        </Box>
      </Box>

      <ParkingsStatsSection
        parkings={parkingsState.parkings}
        companies={managementCompaniesState.companies}
      />

      <Paper sx={{ mb: 2, border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none', borderRadius: 2 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': { backgroundColor: theme.palette.action.hover }
          }}
          onClick={() => setCompaniesExpanded(!companiesExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ color: '#4ECDC4' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Управляючі компанії ({managementCompaniesState.companies.length})
            </Typography>
            {companiesExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCompany(null);
              setCompanyModalOpen(true);
            }}
            sx={{ color: '#4ECDC4' }}
          >
            Додати компанію
          </Button>
        </Box>
        <Collapse in={companiesExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            {managementCompaniesState.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ color: '#4ECDC4' }} />
              </Box>
            ) : managementCompaniesState.companies.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Компанії ще не додані
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {managementCompaniesState.companies.map(company => (
                  <Box
                    key={company._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      borderRadius: '16px',
                      backgroundColor: 'rgba(78, 205, 196, 0.15)',
                      color: '#4ECDC4',
                      pl: 1.5,
                      pr: 0.5,
                      py: 0.25,
                      '&:hover': { backgroundColor: 'rgba(78, 205, 196, 0.25)' }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#3BA99F' }}>
                      {company.name} ({company.commissionPercent}%)
                    </Typography>
                    <Tooltip title="Редагувати">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedCompany(company);
                          setCompanyModalOpen(true);
                        }}
                        sx={{ color: '#3BA99F', p: 0.5 }}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Видалити">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCompanyToDelete(company);
                          setCompanyDeleteCountdown(3);
                        }}
                        sx={{ color: 'rgba(244, 67, 54, 0.7)', p: 0.5, '&:hover': { color: '#F44336' } }}
                      >
                        <Close sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper
          sx={{
            border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Place sx={{ color: '#4ECDC4' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Карта парковок
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title={showParkings ? 'Приховати парковки' : 'Показати парковки'}>
              <IconButton onClick={() => setShowParkings(!showParkings)} size="small">
                {showParkings ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ height: 450 }}>
            {/* Без готового google.maps.Map <GoogleMap> падає і забирає всю сторінку. */}
            {!admins.isGoogleApiInit ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color='text.secondary'>Карта завантажується…</Typography>
              </Box>
            ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={13}
              onLoad={handleMapLoad}
              options={{
                styles: theme.palette.mode === 'dark' ? darkMapStyles : undefined,
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true
              }}
            >
              {showParkings && parkingsState.parkings.map((parking, index) => (
                <React.Fragment key={parking._id}>
                  <Polygon
                    paths={parking.coordinates.map(coord => ({ lat: coord[0], lng: coord[1] }))}
                    options={{
                      fillColor: parking.hiddenInApp ? HIDDEN_PARKING_COLOR : PARKING_COLOR,
                      fillOpacity: parking.hiddenInApp ? 0.15 : 0.3,
                      strokeColor: parking.hiddenInApp ? HIDDEN_PARKING_COLOR : PARKING_COLOR,
                      strokeOpacity: 1,
                      strokeWeight: 2
                    }}
                    onClick={() => handleOpenEdit(parking)}
                  />
                  <Marker
                    position={getPolygonCenter(parking.coordinates as [number, number][])}
                    label={{
                      text: String(index + 1),
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                    title={parking.hiddenInApp ? `${parking.name} (вимкнено)` : parking.name}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 16,
                      fillColor: parking.hiddenInApp ? HIDDEN_PARKING_COLOR : PARKING_COLOR,
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 2
                    }}
                    onClick={() => handleOpenEdit(parking)}
                  />
                  {parking.scooters?.map(scooter => (
                    <Marker
                      key={scooter._id}
                      position={{ lat: scooter.coords.lat, lng: scooter.coords.lon }}
                      title={`${scooter.name} (${scooter.battery}%)`}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: '#4CAF50',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 2
                      }}
                    />
                  ))}
                </React.Fragment>
              ))}
            </GoogleMap>
            )}
          </Box>
        </Paper>

        <Paper
          sx={{
            border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirectionsBike sx={{ color: '#4ECDC4' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Список парковок ({parkingsState.parkings.length})
              </Typography>
            </Box>
            {selectedParkings.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Business />}
                  onClick={() => setBulkAssignDialogOpen(true)}
                  sx={{
                    backgroundColor: '#4ECDC4',
                    '&:hover': { backgroundColor: '#3BA99F' }
                  }}
                >
                  Призначити компанію ({selectedParkings.length})
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Description />}
                  onClick={() => {
                    setBulkDescEn('');
                    setBulkDescUa('');
                    setBulkDescDialogOpen(true);
                  }}
                  sx={{
                    backgroundColor: '#FF9800',
                    '&:hover': { backgroundColor: '#F57C00' }
                  }}
                >
                  Змінити опис ({selectedParkings.length})
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setBulkDeleteCountdown(3);
                    setBulkDeleteDialogOpen(true);
                  }}
                >
                  Видалити ({selectedParkings.length})
                </Button>
              </Box>
            )}
          </Box>
          <TableContainer sx={{ maxHeight: 350 }}>
            {parkingsState.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress sx={{ color: '#4ECDC4' }} />
              </Box>
            ) : parkingsState.parkings.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 2 }}>
                <Place sx={{ fontSize: 48, color: theme.palette.text.disabled }} />
                <Typography color="textSecondary">Парковки ще не додані</Typography>
              </Box>
            ) : (
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell align="center" sx={{ width: 50 }}>
                      <Checkbox
                        size="small"
                        checked={selectedParkings.length === parkingsState.parkings.length && parkingsState.parkings.length > 0}
                        indeterminate={selectedParkings.length > 0 && selectedParkings.length < parkingsState.parkings.length}
                        onChange={handleSelectAllParkings}
                        sx={{ color: '#4ECDC4', '&.Mui-checked': { color: '#4ECDC4' }, '&.MuiCheckbox-indeterminate': { color: '#4ECDC4' } }}
                      />
                    </TableHeaderCell>
                    <TableHeaderCell align="center" sx={{ width: 50 }}>№</TableHeaderCell>
                    <TableHeaderCell>Фото</TableHeaderCell>
                    <TableHeaderCell>Назва</TableHeaderCell>
                    <TableHeaderCell>Компанія</TableHeaderCell>
                    <TableHeaderCell>Адреса</TableHeaderCell>
                    <TableHeaderCell align="center">Самокати</TableHeaderCell>
                    <TableHeaderCell>Оновлено</TableHeaderCell>
                    <TableHeaderCell align="center">Активний</TableHeaderCell>
                    <TableHeaderCell align="center">Дії</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parkingsState.parkings.map((parking, index) => (
                    <StyledTableRow key={parking._id}>
                      <TableCell align="center" sx={{ width: 50 }}>
                        <Checkbox
                          size="small"
                          checked={selectedParkings.includes(parking._id)}
                          onChange={() => handleToggleParkingSelection(parking._id)}
                          sx={{ color: '#4ECDC4', '&.Mui-checked': { color: '#4ECDC4' } }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ width: 50, fontWeight: 600, color: '#4ECDC4' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ width: 80 }}>
                        {parking.photo && getPhotoUrl(parking.photo) ? (
                          <img
                            src={getPhotoUrl(parking.photo)!}
                            alt={parking.name}
                            style={{
                              width: 60,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 4
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 40,
                              backgroundColor: theme.palette.action.hover,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Place sx={{ color: theme.palette.text.disabled }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500 }}>{parking.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {getCompanyName(parking.managementCompany)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {parking.address || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={scooterCount(parking)}
                          size="small"
                          sx={{
                            backgroundColor: scooterCount(parking) > 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                            color: scooterCount(parking) > 0 ? '#4CAF50' : theme.palette.text.secondary
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(parking.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={parking.hiddenInApp
                          ? 'Увімкнути готель'
                          : 'Вимкнути готель: зникне з додатку, біля нього не можна буде завершити поїздку'}>
                          <Switch
                            size="small"
                            checked={!parking.hiddenInApp}
                            onChange={() => handleToggleParkingActive(parking)}
                            sx={activeSwitchSx}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="На карту">
                          <IconButton size="small" onClick={() => focusOnParking(parking)}>
                            <Place fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Редагувати">
                          <IconButton size="small" onClick={() => handleOpenEdit(parking)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Видалити">
                          <IconButton size="small" onClick={() => handleDeleteClick(parking)} color="error">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>
      </Box>

      <ParkingModal
        open={parkingModalOpen}
        onClose={handleCloseModal}
        parking={selectedParking}
        mapCenter={mapCenter}
      />

      <ManagementCompanyModal
        open={companyModalOpen}
        onClose={() => {
          setCompanyModalOpen(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
      />

      <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete} maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" />
          Підтвердження видалення
        </DialogTitle>
        <DialogContent>
          <Typography>
            Ви впевнені, що хочете видалити парковку <strong>{parkingToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Цю дію можна скасувати лише через підтримку.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} startIcon={<Close />}>
            Скасувати
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteCountdown > 0}
            startIcon={<Delete />}
          >
            {deleteCountdown > 0 ? `Видалити (${deleteCountdown})` : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!companyToDelete} onClose={() => setCompanyToDelete(null)} maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" />
          Підтвердження видалення компанії
        </DialogTitle>
        <DialogContent>
          <Typography>
            Ви впевнені, що хочете видалити компанію <strong>{companyToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Парковки, пов'язані з цією компанією, втратять прив'язку.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompanyToDelete(null)} startIcon={<Close />}>
            Скасувати
          </Button>
          <Button
            onClick={() => {
              if (companyToDelete) {
                dispatch(deleteManagementCompanyAsync(companyToDelete._id));
                setCompanyToDelete(null);
              }
            }}
            variant="contained"
            color="error"
            disabled={companyDeleteCountdown > 0}
            startIcon={<Delete />}
          >
            {companyDeleteCountdown > 0 ? `Видалити (${companyDeleteCountdown})` : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={bulkDeleteDialogOpen} 
        onClose={() => {
          setBulkDeleteDialogOpen(false);
          setBulkDeleteCountdown(3);
        }} 
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" />
          Масове видалення парковок
        </DialogTitle>
        <DialogContent>
          <Typography>
            Ви впевнені, що хочете видалити <strong>{selectedParkings.length}</strong> парковок?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Цю дію неможливо скасувати. Всі вибрані парковки будуть видалені назавжди.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBulkDeleteDialogOpen(false);
              setBulkDeleteCountdown(3);
            }} 
            startIcon={<Close />}
          >
            Скасувати
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            color="error"
            disabled={bulkDeleteCountdown > 0 || bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={20} color="inherit" /> : <Delete />}
          >
            {bulkDeleting ? 'Видалення...' : bulkDeleteCountdown > 0 ? `Видалити (${bulkDeleteCountdown})` : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={kmlImportDialogOpen} onClose={handleCancelKmlImport} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileUpload sx={{ color: '#4ECDC4' }} />
            Імпорт парковок з KML
          </Box>
          <IconButton onClick={handleCancelKmlImport} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Файл: <strong>{pendingKmlFile?.name}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Виберіть управляючу компанію для всіх імпортованих парковок:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Управляюча компанія *</InputLabel>
            <Select
              value={kmlImportCompany}
              onChange={(e) => setKmlImportCompany(e.target.value as string)}
              label="Управляюча компанія *"
            >
              {managementCompaniesState.companies.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {managementCompaniesState.companies.length === 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Спочатку створіть управляючу компанію в розділі нижче
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelKmlImport} startIcon={<Close />}>
            Скасувати
          </Button>
          <Button
            onClick={handleConfirmKmlImport}
            variant="contained"
            disabled={!kmlImportCompany || importing}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <FileUpload />}
            sx={{ backgroundColor: '#4ECDC4', '&:hover': { backgroundColor: '#3BA99F' } }}
          >
            {importing ? 'Імпорт...' : 'Імпортувати'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={bulkDescDialogOpen} 
        onClose={() => !bulkDescUpdating && setBulkDescDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: '#FF9800' }} />
            Змінити опис
          </Box>
          <IconButton onClick={() => setBulkDescDialogOpen(false)} size="small" disabled={bulkDescUpdating}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Вибрано парковок: <strong>{selectedParkings.length}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Введіть новий опис (інструкції) для всіх вибраних парковок. Заповніть одну або обидві мови.
          </Typography>
          <TextField
            fullWidth
            label="Опис (EN)"
            placeholder="Instructions in English..."
            multiline
            rows={2}
            value={bulkDescEn}
            onChange={(e) => setBulkDescEn(e.target.value)}
            sx={{ mb: 2 }}
            disabled={bulkDescUpdating}
          />
          <TextField
            fullWidth
            label="Опис (UA)"
            placeholder="Інструкції українською..."
            multiline
            rows={2}
            value={bulkDescUa}
            onChange={(e) => setBulkDescUa(e.target.value)}
            sx={{ mb: 2 }}
            disabled={bulkDescUpdating}
          />
          <TextField
            fullWidth
            label="Опис (DE)"
            placeholder="Anweisungen auf Deutsch..."
            multiline
            rows={2}
            value={bulkDescDe}
            onChange={(e) => setBulkDescDe(e.target.value)}
            sx={{ mb: 2 }}
            disabled={bulkDescUpdating}
          />
          <TextField
            fullWidth
            label="Опис (ES)"
            placeholder="Instrucciones en español..."
            multiline
            rows={2}
            value={bulkDescEs}
            onChange={(e) => setBulkDescEs(e.target.value)}
            disabled={bulkDescUpdating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDescDialogOpen(false)} startIcon={<Close />} disabled={bulkDescUpdating}>
            Скасувати
          </Button>
          <Button
            onClick={handleBulkDescUpdate}
            variant="contained"
            disabled={(!bulkDescEn.trim() && !bulkDescUa.trim() && !bulkDescDe.trim() && !bulkDescEs.trim()) || bulkDescUpdating}
            startIcon={bulkDescUpdating ? <CircularProgress size={16} color="inherit" /> : <Description />}
            sx={{ backgroundColor: '#FF9800', '&:hover': { backgroundColor: '#F57C00' } }}
          >
            {bulkDescUpdating ? 'Оновлення...' : 'Оновити опис'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkAssignDialogOpen} onClose={() => setBulkAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ color: '#4ECDC4' }} />
            Призначити компанію
          </Box>
          <IconButton onClick={() => setBulkAssignDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Вибрано парковок: <strong>{selectedParkings.length}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Виберіть управляючу компанію для призначення:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Управляюча компанія *</InputLabel>
            <Select
              value={bulkAssignCompany}
              onChange={(e) => setBulkAssignCompany(e.target.value as string)}
              label="Управляюча компанія *"
            >
              {managementCompaniesState.companies.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignDialogOpen(false)} startIcon={<Close />}>
            Скасувати
          </Button>
          <Button
            onClick={handleBulkAssign}
            variant="contained"
            disabled={!bulkAssignCompany || bulkAssigning}
            startIcon={bulkAssigning ? <CircularProgress size={16} color="inherit" /> : <Business />}
            sx={{ backgroundColor: '#4ECDC4', '&:hover': { backgroundColor: '#3BA99F' } }}
          >
            {bulkAssigning ? 'Призначення...' : 'Призначити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default ParkingsPage;
